// Central RxJS bus for the whiteboard feature.
//
// Exposes 7 streams plus a `destroy$` lifecycle Subject:
//   - destroy$              : fires once on `destroy()`, then completes (TR4)
//   - uiActions$            : fire-and-forget UI intents (P1: type-only; see ui-action.events.ts)
//   - domain$               : domain mutations from BoardState (object-added/updated/removed/reordered)
//   - selection$            : current selection IDs (BehaviorSubject, initial = [])
//   - tool$                 : current tool (BehaviorSubject, initial = 'select')
//   - viewport$             : current viewport (BehaviorSubject, initial = {x:0, y:0, scale:1})
//   - liveTransform$        : per-object live drag/resize ticks (PRD-H §TR3, Subject)
//   - stagedToolProperties$ : per-tool pre-placement property choices (Stefan, 2026-09-03)
//
// `destroy()` MUST emit `destroy$.next()` BEFORE `destroy$.complete()` so that
// `takeUntil(bus.destroy$)`-pipelines unsubscribe cleanly (FC-5). All 8 subjects
// are completed on destroy() to prevent late writes (FC-6).

import { BehaviorSubject, Subject } from 'rxjs';

import type { Tool } from '../../core/tool/tool.types.js';
import type { ObjectId } from './branded-types.js';
import type { DomainEvent } from './domain-events.js';
import type { UiAction } from './ui-action.events.js';

export interface Viewport {
    readonly x: number;
    readonly y: number;
    readonly scale: number;
}

/**
 * PRD-H §TR3: payload for live drag/resize updates emitted by `fireLiveTransform`.
 * Only carries the object id — Subject (not BehaviorSubject), emitted only during
 * active drag/resize gestures. Subscribers that need positional data look up
 * the live Konva node via `screenBboxFor(id)` / `boardState.getObject(id)`.
 */
export interface LiveTransformPayload {
    readonly id: ObjectId;
}

/** A tool's pre-placement property choices, keyed by the resolved BoardObject
 *  property name (e.g. `'icon'`, `'color'`, `'strokeColor'`) — the same names
 *  `resolveProperty(intent, type)` in `optionsbar-config.ts` already produces
 *  for the post-creation editing path. */
export type StagedToolProperties = Readonly<Record<string, string | number>>;

/**
 * UiActions, die auf einem gesperrten Board (`readonly`) verworfen werden.
 *
 * **Das Kriterium.** Hier steht eine Aktion genau dann, wenn ihr Publisher
 * `readonly` NICHT selbst prüft. Das sind heute drei: der
 * `KeyboardShortcutsController`, das native `paste`-Ereignis
 * (`ImagePasteHandler`) und Klicks auf gerenderte Knoten (`textbox.renderer`).
 * Sie alle veröffentlichen blind, also fängt der Bus sie ab.
 *
 * **Nicht** das Kriterium ist „braucht keine Auswahl" — `object/delete`,
 * `object/nudge` und `clipboard/cut` stehen hier und setzen sehr wohl eine
 * Auswahl voraus. Umgekehrt fehlen `object/update-property`,
 * `object/duplicate`, `object/z-order`, `line/*`, `textbox/apply-*` und
 * `edit/commit*`, und zwar zu Recht: Ihre Publisher schützen sich selbst.
 * Die beiden Netze, auf die sich das stützt, sind
 *   1. die Optionsbar, die bei `board.readonly` gar nichts rendert (TR8), und
 *   2. die Griff-Controller (Linien-Endpunkte, Kurve, Mittelsegment,
 *      Konnektoren), die beim Aufbau `getReadonly()` prüfen.
 * `object/update-property`, `object/duplicate` und `object/z-order` haben
 * darüber hinaus derzeit überhaupt keinen Abonnenten.
 *
 * **Wer einen Publisher ergänzt**, prüft also zuerst, ob dieser `readonly`
 * selbst kennt. Wenn nicht, gehört seine Aktion hier hinein.
 *
 * Bewusst offen: `clipboard/copy` (liest nur), `selection/set`,
 * `selection/add`, `selection/clear`, `tool/set` und `viewport/*` — nichts
 * davon ändert den Objektbestand, und `tool/set` ist unter `lockToSelect()`
 * ohnehin ein No-op. `selection/select-all` ist die Ausnahme in dieser Reihe:
 * Strg+A baut die Auswahl, die das Sperren gerade abgeräumt hat, in einem Zug
 * wieder auf — und genau darüber lief der Datenverlust (Strg+A, dann Entf).
 *
 * Ebenfalls offen: `place/sticky` und die übrigen `place/*` außer
 * `place/image` — sie brauchen ein Zeichenwerkzeug, und `readonly` sperrt das
 * Werkzeug auf `select`. `place/image` ist die Ausnahme, weil es am nativen
 * `paste`-Ereignis hängt und deshalb ganz ohne Werkzeug erreichbar ist.
 *
 * Der Typ `UiAction['type']` schützt vor Tippfehlern in den Einträgen — mehr
 * nicht. Ein neues Union-Mitglied in `ui-action.events.ts` übersetzt ohne jede
 * Änderung hier; der Compiler erzwingt die Entscheidung also **nicht**.
 */
export const READONLY_BLOCKED_UI_ACTIONS: ReadonlySet<UiAction['type']> = new Set([
    'object/delete',
    'object/nudge',
    'clipboard/cut',
    'clipboard/paste',
    'clipboard/paste-text',
    'selection/select-all',
    // Review 0023, Lauf 02: erreichbar OHNE Zeichenwerkzeug, deshalb hier.
    // `place/image` kommt aus dem nativen paste-Ereignis (Screenshot per
    // Strg+V), die beiden textbox-Aktionen aus Klicks auf gerenderte Knoten
    // (Checklisten-Häkchen bzw. der „+"-Überlaufmarker).
    'place/image',
    'textbox/toggle-checklist-item',
    'textbox/fit-to-content',
    // Diese beiden werden heute von KEINEM Abonnenten konsumiert — Strg+Z/
    // Strg+Y wirken über die Rückrufe von `registerUndoRedoShortcuts`, die
    // ihren eigenen Guard haben. Sie stehen trotzdem hier, damit das
    // Versprechen des Docblocks oben stimmt: zieht jemand später den seit
    // Langem angekündigten Bridge-Fall nach, umgeht Strg+Z die Sperre nicht.
    'history/undo',
    'history/redo',
]);

/**
 * `Subject`, das auf einem gesperrten Board verändernde Aktionen gar nicht
 * erst weitergibt.
 *
 * Der Guard sitzt an `next()`, also an der EINEN Stelle, durch die jede
 * Veröffentlichung läuft — und nicht in einem einzelnen `subscribe`. Das ist
 * der Unterschied zur ersten Fassung (Review 0023, Lauf 01): `uiActions$` hat
 * rund zwanzig Abonnenten, von denen die meisten eigene Controller sind. Ein
 * Guard im Abonnement des Board-Elements schützte nur dessen eigenen
 * `switch`; `place/image` (ImagePlacementController) und die beiden
 * textbox-Aktionen (TextboxLifecycleController) liefen daran vorbei, und ein
 * Screenshot per Strg+V veränderte weiterhin ein gesperrtes Board.
 *
 * Achtung: Das hält NICHT den Upload eines eingefügten Bildes auf — der läuft
 * im `ImagePasteHandler` VOR dieser Veröffentlichung. Dort sitzt deshalb ein
 * eigener Guard.
 */
class UiActionSubject extends Subject<UiAction> {
    #readonly = false;

    get readonly(): boolean {
        return this.#readonly;
    }

    setReadonly(value: boolean): void {
        this.#readonly = value;
    }

    override next(action: UiAction): void {
        if (this.#readonly && READONLY_BLOCKED_UI_ACTIONS.has(action.type)) return;
        super.next(action);
    }
}

export class WhiteboardBus {
    readonly destroy$ = new Subject<void>();
    readonly uiActions$ = new UiActionSubject();
    readonly domain$ = new Subject<DomainEvent>();
    readonly selection$ = new BehaviorSubject<readonly ObjectId[]>([]);
    readonly tool$ = new BehaviorSubject<Tool>('select');
    readonly viewport$ = new BehaviorSubject<Viewport>({ x: 0, y: 0, scale: 1 });
    readonly liveTransform$ = new Subject<LiveTransformPayload>();

    /**
     * Stefan, 2026-09-03 — "choose properties BEFORE drawing" (second path
     * alongside the existing "draw, then edit via the options circle" one).
     * Keyed by `Tool`, one `StagedToolProperties` bag per tool. Deliberately
     * NOT cleared on tool change: switching tools and back keeps the last
     * choice, mirroring how `tool$` itself just sits at its last value until
     * explicitly changed — no separate "reset" concept exists for it either.
     */
    readonly stagedToolProperties$ = new BehaviorSubject<Partial<Record<Tool, StagedToolProperties>>>({});

    /**
     * Schaltet die Board-Sperre für den Bus (Review 0023, Lauf 02). Wird vom
     * Element aus `#applyReadonly()` gesetzt und gilt ab dann für JEDEN
     * Abonnenten, auch für später hinzugekommene.
     */
    setReadonly(value: boolean): void {
        this.uiActions$.setReadonly(value);
    }

    /**
     * Ist das Board gesperrt? Für die wenigen Stellen, die schon VOR der
     * Veröffentlichung wissen müssen, ob sie etwas tun dürfen — namentlich der
     * `ImagePasteHandler`, dessen Upload dem Bus-Ereignis vorausgeht.
     */
    get readonly(): boolean {
        return this.uiActions$.readonly;
    }

    /** Merges one property into the given tool's staged bag (immutable
     *  update — a NEW top-level object AND a new per-tool bag, so `distinct`/
     *  identity-based consumers still see the change). */
    setStagedToolProperty(tool: Tool, property: string, value: string | number): void {
        const current = this.stagedToolProperties$.value;
        this.stagedToolProperties$.next({
            ...current,
            [tool]: { ...current[tool], [property]: value },
        });
    }

    /** Removes one property from the given tool's staged bag (Review-Lauf 01,
     *  2026-09-03 — mirrors the post-creation TOGGLE_INTENTS behaviour: a
     *  second click on an already-staged toggle value un-stages it, instead
     *  of being stuck re-setting the same value). */
    clearStagedToolProperty(tool: Tool, property: string): void {
        const current = this.stagedToolProperties$.value;
        const bag = current[tool];
        if (!bag || !(property in bag)) return;
        const { [property]: _removed, ...rest } = bag;
        this.stagedToolProperties$.next({ ...current, [tool]: rest });
    }

    /**
     * Tear down the bus. MUST be called from the host's `disconnectedCallback`
     * AFTER all adapters have been disposed (TR9 / FC-6 ordering).
     *
     * Order is mandatory:
     *   1. destroy$.next()      — signals `takeUntil(bus.destroy$)` consumers
     *   2. destroy$.complete()  — closes the lifecycle stream itself
     *   3. complete remaining 7 — prevents any late `.next()` from leaking
     */
    destroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.uiActions$.complete();
        this.domain$.complete();
        this.selection$.complete();
        this.tool$.complete();
        this.viewport$.complete();
        this.liveTransform$.complete();
        this.stagedToolProperties$.complete();
    }
}
